import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  useTimeEntries,
  useMergedCategories,
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useCreateTimeCategory,
  useDeleteTimeCategory,
  DEFAULT_CATEGORIES,
} from '../helpers/useTimeEntries';
import { useSessionsList } from '../helpers/useSessionsApi';
import styles from './timekeeper.module.css';
import { useAuth } from '../helpers/useAuth';
import { OnboardingTutorial, useShowSectionOnboarding } from '../components/OnboardingTutorial';

/* ─── Helpers ─── */
const fmt = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const fmtDur = (s: number) => {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const fmtRange = (a: string, b: string) => {
  const f = (t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${f(a)} — ${f(b)}`;
};

const RADIUS = 118;
const CIRC = 2 * Math.PI * RADIUS;

const PROJECT_COLORS = [
  '#4a7cdb', '#c76b8a', '#c9a84c', '#3a9a8a', '#6aab6a',
  '#9a5ad4', '#d4654a', '#4ad4b0', '#db7c4a', '#7c4adb',
];

/* ─── SVG Ticks ─── */
function Ticks() {
  return (
    <>
      {Array.from({ length: 60 }, (_, i) => {
        const angle = ((i * 6) - 90) * (Math.PI / 180);
        const major = i % 5 === 0;
        const r1 = major ? 103 : 107;
        const r2 = major ? 93 : 100;
        return (
          <line
            key={i}
            x1={140 + r1 * Math.cos(angle)} y1={140 + r1 * Math.sin(angle)}
            x2={140 + r2 * Math.cos(angle)} y2={140 + r2 * Math.sin(angle)}
            stroke={major ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.1)'}
            strokeWidth={major ? 1.5 : 0.7}
          />
        );
      })}
    </>
  );
}

/* ─── Page ─── */
export default function TimekeeperPage() {
  const { authState } = useAuth();
  const currentUserId = authState.type === 'authenticated' ? authState.user?.id : null;
  const showTutorial = useShowSectionOnboarding('timekeeper', currentUserId);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const { data: sessionsData } = useSessionsList('active');
  const sessions = sessionsData?.sessions || [];
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');

  const { data: entriesData } = useTimeEntries(selectedSessionId || undefined);
  const categories = useMergedCategories();
  const createEntry = useCreateTimeEntry();
  const deleteEntry = useDeleteTimeEntry();
  const createCat = useCreateTimeCategory();
  const deleteCat = useDeleteTimeCategory();

  const entries = entriesData?.entries || [];

  /* Timer */
  const [timerState, setTimerState] = useState<'idle' | 'running' | 'paused'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [sessionStart, setSessionStart] = useState<number | null>(null);
  const timerStartRef = useRef<number | null>(null);
  const pausedRef = useRef(0);
  const intervalRef = useRef<number | null>(null);

  const [activeCat, setActiveCat] = useState(categories[0]?.name || 'Development');
  const [activeCatColor, setActiveCatColor] = useState(categories[0]?.color || '#4a7cdb');
  const [note, setNote] = useState('');
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => { document.title = 'Chronograph — Timekeeper'; }, []);

  useEffect(() => {
    if (timerState === 'running' && timerStartRef.current) {
      intervalRef.current = window.setInterval(() => {
        setElapsed(pausedRef.current + Math.floor((Date.now() - timerStartRef.current!) / 1000));
      }, 200);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timerState]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'SELECT') return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (timerState === 'idle') startTimer();
        else if (timerState === 'running') pauseTimer();
        else startTimer();
      }
      if (e.code === 'Escape' && timerState !== 'idle') stopTimer();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // Auto-select first session
  useEffect(() => {
    if (!selectedSessionId && sessions.length > 0) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [sessions, selectedSessionId]);

  const startTimer = () => {
    if (!selectedSessionId) return;
    const now = Date.now();
    if (timerState === 'idle') { setSessionStart(now); pausedRef.current = 0; }
    timerStartRef.current = now;
    setTimerState('running');
  };

  const pauseTimer = () => {
    pausedRef.current = elapsed;
    timerStartRef.current = null;
    setTimerState('paused');
  };

  const stopTimer = () => {
    if (elapsed > 0 && sessionStart && selectedSessionId) {
      createEntry.mutate({
        sessionId: selectedSessionId,
        category: activeCat,
        categoryColor: activeCatColor,
        note: note.trim(),
        startTime: new Date(sessionStart).toISOString(),
        endTime: new Date().toISOString(),
        duration: elapsed,
      });
    }
    setTimerState('idle');
    setElapsed(0);
    pausedRef.current = 0;
    timerStartRef.current = null;
    setSessionStart(null);
    setNote('');
  };

  const addCategory = () => {
    if (!newCatName.trim()) return;
    const color = PROJECT_COLORS[categories.length % PROJECT_COLORS.length];
    createCat.mutate({ name: newCatName.trim(), color });
    setNewCatName('');
    setShowAddCat(false);
  };

  /* Stats */
  const today = new Date().toDateString();
  const todayEntries = entries.filter(e => new Date(e.startTime).toDateString() === today);
  const todayTotal = todayEntries.reduce((s, e) => s + e.duration, 0);

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekEntries = entries.filter(e => new Date(e.startTime).getTime() >= weekStart.getTime());
  const weekTotal = weekEntries.reduce((s, e) => s + e.duration, 0);

  const progress = Math.min(elapsed / 3600, 1);
  const dashOffset = CIRC * (1 - progress);

  const breakdown = useMemo(() => {
    const map = new Map<string, { color: string; total: number }>();
    for (const e of todayEntries) {
      const ex = map.get(e.category);
      if (ex) ex.total += e.duration;
      else map.set(e.category, { color: e.categoryColor, total: e.duration });
    }
    return Array.from(map.entries()).map(([name, { color, total }]) => ({ name, color, total })).sort((a, b) => b.total - a.total);
  }, [todayEntries]);

  /* Group by date */
  const grouped = useMemo(() => {
    const groups: { date: string; items: typeof entries }[] = [];
    for (const e of entries) {
      const d = new Date(e.startTime).toDateString();
      const last = groups[groups.length - 1];
      if (last && last.date === d) last.items.push(e);
      else groups.push({ date: d, items: [e] });
    }
    return groups;
  }, [entries]);

  return (
    <>
      {showTutorial && !tutorialDismissed && (
        <OnboardingTutorial
          type="timekeeper"
          userId={currentUserId}
          onComplete={() => setTutorialDismissed(true)}
        />
      )}
      <div className={styles.page}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Chronograph</h1>
            <span className={styles.subtitle}>Precision Time Tracking</span>
          </div>
          <div className={styles.headerStats}>
            <div className={styles.statPill}>
              <span className={styles.statLabel}>Today</span>
              <span className={styles.statVal}>{fmtDur(todayTotal + (timerState !== 'idle' ? elapsed : 0))}</span>
            </div>
            <div className={styles.statPill}>
              <span className={styles.statLabel}>Week</span>
              <span className={styles.statVal}>{fmtDur(weekTotal + (timerState !== 'idle' ? elapsed : 0))}</span>
            </div>
            <div className={styles.statPill}>
              <span className={styles.statLabel}>Entries</span>
              <span className={styles.statVal}>{todayEntries.length}</span>
            </div>
          </div>
        </header>

        <main className={styles.main}>
          {/* Session selector */}
          <select className={styles.sessionSelect} value={selectedSessionId} onChange={e => setSelectedSessionId(e.target.value)}>
            <option value="">All sessions</option>
            {sessions.map((s: any) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Timer */}
          <section className={styles.timerSection}>
            <div className={styles.timerWrap}>
              <svg className={styles.timerSvg} viewBox="0 0 280 280">
                <Ticks />
                <circle cx="140" cy="140" r={RADIUS} fill="none" stroke="rgba(201,168,76,0.06)" strokeWidth="5" />
                <circle
                  className={styles.timerArc}
                  cx="140" cy="140" r={RADIUS}
                  fill="none" stroke={activeCatColor}
                  strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={CIRC} strokeDashoffset={dashOffset}
                  transform="rotate(-90 140 140)"
                  style={{ filter: `drop-shadow(0 0 8px ${activeCatColor}40)` }}
                />
              </svg>
              <div className={styles.timerFace}>
                <div className={`${styles.digits} ${timerState === 'paused' ? styles.blink : ''}`}>{fmt(elapsed)}</div>
                <div className={styles.catLabel} style={{ color: activeCatColor }}>{activeCat}</div>
                {timerState === 'running' && <div className={styles.pulse} style={{ borderColor: activeCatColor }} />}
              </div>
            </div>

            <input
              className={styles.noteInput}
              placeholder="What are you working on?"
              value={note} onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && timerState === 'idle') startTimer(); }}
            />

            <div className={styles.controls}>
              {timerState === 'idle' && (
                <button className={`${styles.btn} ${styles.btnStart}`} onClick={startTimer} disabled={!selectedSessionId}>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><polygon points="5,3 17,10 5,17" /></svg>
                  Start
                </button>
              )}
              {timerState === 'running' && (
                <>
                  <button className={`${styles.btn} ${styles.btnPause}`} onClick={pauseTimer}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                      <rect x="4" y="3" width="4" height="14" rx="1" /><rect x="12" y="3" width="4" height="14" rx="1" />
                    </svg>
                    Pause
                  </button>
                  <button className={`${styles.btn} ${styles.btnStop}`} onClick={stopTimer}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><rect x="4" y="4" width="12" height="12" rx="2" /></svg>
                    Stop
                  </button>
                </>
              )}
              {timerState === 'paused' && (
                <>
                  <button className={`${styles.btn} ${styles.btnStart}`} onClick={startTimer}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><polygon points="5,3 17,10 5,17" /></svg>
                    Resume
                  </button>
                  <button className={`${styles.btn} ${styles.btnStop}`} onClick={stopTimer}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><rect x="4" y="4" width="12" height="12" rx="2" /></svg>
                    Stop
                  </button>
                </>
              )}
            </div>

            <p className={styles.shortcutHint}>
              <kbd className={styles.kbd}>Space</kbd> start / pause
              <kbd className={styles.kbd}>Esc</kbd> stop
            </p>

            {/* Categories */}
            <div className={styles.chipBar}>
              {categories.map(c => (
                <button
                  key={c.id || c.name}
                  className={`${styles.chip} ${activeCat === c.name ? styles.chipActive : ''}`}
                  onClick={() => { setActiveCat(c.name); setActiveCatColor(c.color); }}
                  onContextMenu={e => {
                    e.preventDefault();
                    if (!DEFAULT_CATEGORIES.some(d => d.name === c.name)) {
                      setDeleteConfirm(deleteConfirm === c.id ? null : c.id);
                    }
                  }}
                  style={{ borderColor: activeCat === c.name ? c.color : 'transparent', background: activeCat === c.name ? `${c.color}15` : undefined }}
                >
                  <span className={styles.chipDot} style={{ background: c.color }} />
                  {c.name}
                  {deleteConfirm === c.id && (
                    <span className={styles.chipDelete} onClick={e => { e.stopPropagation(); deleteCat.mutate({ id: c.id }); setDeleteConfirm(null); }}>
                      Remove
                    </span>
                  )}
                </button>
              ))}
              {!showAddCat ? (
                <button className={`${styles.chip} ${styles.chipAdd}`} onClick={() => setShowAddCat(true)}>+ Add</button>
              ) : (
                <form className={styles.addForm} onSubmit={e => { e.preventDefault(); addCategory(); }}>
                  <input autoFocus className={styles.addInput} placeholder="Name" value={newCatName} onChange={e => setNewCatName(e.target.value)} onBlur={() => { if (!newCatName.trim()) setShowAddCat(false); }} />
                  <button type="submit" className={styles.addBtn}>+</button>
                </form>
              )}
            </div>
          </section>

          {/* Bottom */}
          <div className={styles.bottom}>
            {/* Breakdown */}
            {breakdown.length > 0 && (
              <section className={styles.breakdownCard}>
                <h2 className={styles.secTitle}>Today's Breakdown</h2>
                <div className={styles.barTrack}>
                  {breakdown.map(b => (
                    <div key={b.name} className={styles.barSeg} style={{ width: `${(b.total / (todayTotal || 1)) * 100}%`, background: b.color }} title={`${b.name}: ${fmtDur(b.total)}`} />
                  ))}
                </div>
                <div className={styles.legend}>
                  {breakdown.map(b => (
                    <div key={b.name} className={styles.legendItem}>
                      <span className={styles.legendDot} style={{ background: b.color }} />
                      <span className={styles.legendName}>{b.name}</span>
                      <span className={styles.legendDur}>{fmtDur(b.total)}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Entries */}
            <section className={styles.entriesCard}>
              <h2 className={styles.secTitle}>
                Time Entries
                {entries.length > 0 && <span className={styles.badge}>{entries.length}</span>}
              </h2>
              {entries.length === 0 ? (
                <div className={styles.empty}>
                  <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.2">
                    <circle cx="24" cy="24" r="20" />
                    <line x1="24" y1="24" x2="24" y2="12" strokeLinecap="round" />
                    <line x1="24" y1="24" x2="32" y2="28" strokeLinecap="round" />
                  </svg>
                  <p>Start tracking to see entries</p>
                </div>
              ) : (
                <div className={styles.entryList}>
                  {grouped.map(group => (
                    <div key={group.date} className={styles.entryGroup}>
                      <div className={styles.dateHead}>
                        {group.date === today ? 'Today' : group.date === new Date(Date.now() - 86400000).toDateString() ? 'Yesterday' : new Date(group.items[0].startTime).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                        <span className={styles.dateTotal}>{fmtDur(group.items.reduce((s, e) => s + e.duration, 0))}</span>
                      </div>
                      {group.items.map(entry => (
                        <div key={entry.id} className={styles.entryRow}>
                          <div className={styles.entryBar} style={{ background: entry.categoryColor }} />
                          <div className={styles.entryBody}>
                            <div className={styles.entryTop}>
                              <span className={styles.entryProj} style={{ color: entry.categoryColor }}>{entry.category}</span>
                              {entry.note && <span className={styles.entryNote}>{entry.note}</span>}
                              {entry.sessionName && !selectedSessionId && (
                                <span className={styles.entrySession}>{entry.sessionName}</span>
                              )}
                            </div>
                            <div className={styles.entryMeta}>
                              <span className={styles.entryRange}>{fmtRange(entry.startTime, entry.endTime)}</span>
                              <span className={styles.entryDur}>{fmtDur(entry.duration)}</span>
                            </div>
                            {entry.todoTitle && <div className={styles.entryTodo}>Todo: {entry.todoTitle}</div>}
                          </div>
                          <button className={styles.entryDel} onClick={() => deleteEntry.mutate({ id: entry.id })}>×</button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </>
  );
}
